import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Alert, alertsApi, handleApiError } from '../services/alertsService';
import AlertItem from './AlertItem';
import { Bell, CheckCircle, X } from 'lucide-react';

interface AlertsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onAlertsUpdated: () => void;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 420px;
  max-width: calc(100vw - 40px);
  max-height: 600px;
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid var(--border-color);
  z-index: 1002;
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease-out;
  overflow: hidden;

  @media (max-width: 768px) {
    width: calc(100vw - 32px);
    right: 16px;
    max-height: calc(100vh - 100px);
  }
`;

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--background-color);
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-secondary);
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-sm);
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
    color: var(--text-primary);
  }
`;

const AlertsList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 450px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--background-color);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-tertiary);
  }
`;

const Footer = styled.div`
  padding: 12px 20px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: center;
  background: var(--background-color);
`;

const MarkAllButton = styled.button`
  padding: 8px 16px;
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: var(--primary-light);
    color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  padding: 60px 40px;
  text-align: center;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  margin-bottom: 16px;
  color: var(--success-color);
  opacity: 0.7;
`;

const EmptyTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const EmptyDescription = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const LoadingState = styled.div`
  padding: 40px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
`;

const ErrorState = styled.div`
  padding: 20px;
  margin: 16px;
  background: var(--error-light);
  color: var(--error-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  text-align: center;
`;

const AlertsDropdown: React.FC<AlertsDropdownProps> = ({ isOpen, onClose, onAlertsUpdated }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadAlerts();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Check if click is not on the bell button
        const bellButton = document.querySelector('[data-bell-button]');
        if (bellButton && !bellButton.contains(event.target as Node)) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await alertsApi.getUnreadAlerts();
      // Limit to 10 most recent
      setAlerts(data.slice(0, 10));
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAlertRead = (alertId: number) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    onAlertsUpdated();
  };

  const handleAlertHandled = (alertId: number) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    onAlertsUpdated();
  };

  const handleAlertDelete = (alertId: number) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
    onAlertsUpdated();
  };

  const handleMarkAllAsRead = async () => {
    if (alerts.length === 0) return;

    try {
      setMarkingAll(true);
      const alertIds = alerts.map(a => a.id);
      await alertsApi.markAllAsRead(alertIds);
      setAlerts([]);
      onAlertsUpdated();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setMarkingAll(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <LoadingState>טוען התראות...</LoadingState>;
    }

    if (error) {
      return <ErrorState>{error}</ErrorState>;
    }

    if (alerts.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon><CheckCircle size={48} /></EmptyIcon>
          <EmptyTitle>אין התראות חדשות</EmptyTitle>
          <EmptyDescription>כל ההתראות נקראו. מצוין!</EmptyDescription>
        </EmptyState>
      );
    }

    return (
      <>
        <AlertsList>
          {alerts.map(alert => (
            <AlertItem
              key={alert.id}
              alert={alert}
              onRead={handleAlertRead}
              onHandled={handleAlertHandled}
              onDelete={handleAlertDelete}
            />
          ))}
        </AlertsList>

        <Footer>
          <MarkAllButton
            onClick={handleMarkAllAsRead}
            disabled={markingAll || alerts.length === 0}
          >
            {markingAll ? 'מסמן הכל...' : 'סמן הכל כנקרא'}
          </MarkAllButton>
        </Footer>
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <Container $isOpen={isOpen} ref={containerRef}>
      <Header>
        <Title>
          <Bell size={18} />
          התראות
          {alerts.length > 0 && ` (${alerts.length})`}
        </Title>
        <CloseButton onClick={onClose} aria-label="סגור">
          <X size={18} />
        </CloseButton>
      </Header>

      {renderContent()}
    </Container>
  );
};

export default AlertsDropdown;
